/**
 * WebAssembly capability detection (issue #943).
 *
 * Orion's E2EE crypto (`ow2_crypto`) is WASM-only. Some environments make WASM
 * unavailable and cause an opaque boot crash:
 *   - iOS Lockdown Mode removes the `WebAssembly` global entirely
 *     (`ReferenceError: can't find variable: WebAssembly`).
 *   - Managed / enterprise browsers (e.g. "Island") allow the `WebAssembly`
 *     object to exist but block code generation, so compiling a module throws
 *     `CompileError: wasm code generation disallowed by embedder`.
 *
 * This helper detects BOTH cases synchronously and defensively (any unexpected
 * throw is treated as "unavailable"). It has no imports and no side effects so it
 * is safe to call at the very top of the boot path, before the crypto module runs.
 */

// Smallest valid WebAssembly module: the 4-byte magic header (\0asm) followed by
// the 4-byte version (1). Compiling this is enough to trigger an embedder's
// "codegen disallowed" policy without doing any real work.
const EMPTY_WASM_MODULE = new Uint8Array([
  0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00,
]);

export function isWebAssemblyAvailable(): boolean {
  try {
    if (
      typeof WebAssembly !== "object" ||
      WebAssembly === null ||
      typeof WebAssembly.instantiate !== "function" ||
      typeof WebAssembly.Module !== "function"
    ) {
      return false;
    }

    // Compile probe: catches embedders that expose the API but disallow codegen.
    // Throws synchronously (CompileError) when blocked; succeeds otherwise.
    new WebAssembly.Module(EMPTY_WASM_MODULE);

    return true;
  } catch {
    return false;
  }
}
